import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getStudents } from '../services/studentService';
import { useAuth } from './AuthContext';
import { useCacheInvalidation } from './CacheInvalidationContext';

const StudentContext = createContext();

export const useStudents = () => {
    const context = useContext(StudentContext);
    if (!context) {
        throw new Error('useStudents must be used within a StudentProvider');
    }
    return context;
};

export const StudentProvider = ({ children }) => {
    const { center, hasPermission } = useAuth();
    const { registerRefresh } = useCacheInvalidation();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchStudents = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Skip fetch if user doesn't have permission
            if (!hasPermission('student:list')) {
                setStudents([]);
                return;
            }

            const data = await getStudents();
            setStudents(data);
        } catch (err) {
            console.error('Failed to fetch students:', err);
            setError(err);
            setStudents([]);
        } finally {
            setLoading(false);
        }
    }, [hasPermission]);

    // Fetch students when center changes
    useEffect(() => {
        fetchStudents();
    }, [fetchStudents]);

    // Poll for updates every 10 minutes (when page is visible)
    useEffect(() => {
        const pollInterval = setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchStudents();
            }
        }, 10 * 60 * 1000); // 10 minutes

        return () => clearInterval(pollInterval);
    }, [fetchStudents]);

    // Refresh function to be called after CRUD operations
    const refreshStudents = useCallback(() => {
        fetchStudents();
    }, [fetchStudents]);

    // Register refresh function for cache invalidation
    useEffect(() => {
        const cleanup = registerRefresh('students', refreshStudents);
        return cleanup;
    }, [registerRefresh, refreshStudents]);

    const value = {
        students,
        loading,
        error,
        refreshStudents,
        fetchStudents
    };

    return (
        <StudentContext.Provider value={value}>
            {children}
        </StudentContext.Provider>
    );
};

export default StudentContext;
